import { FileService, eFileService } from "../../../services/SubmissionService";
import { OrganizationService } from "../../../services/ClientService";
import * as APIModels from "../../../models";
import pdfIcon from "../../../assets/images/file-icons/pdf.svg";
import zipIcon from "../../../assets/images/file-icons/zip.svg";
import React, { useRef, useState, useCallback, useEffect } from "react";
import { formatBytes } from "../../../helpers/utility";
import { Globals } from "../../../helpers/globals";
import { useDropzone } from "react-dropzone";
import {
    ErrorModal,
    ErrorModalProps,
} from "../../../components/shared/modals/ErrorModal";
import { authenticationService } from "../../../services/authentication.service";
import { Endpoints } from "../../../helpers/Endpoints";

export default function SplittingManager() {
    const f: APIModels.FileInfo[] = [];
    const [files, setFiles] = useState(f);
    const [organization, setOrganization] = useState(
        new APIModels.Organization()
    );
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedFiles, setSelectedFiles] = useState(f);
    const iframeRef = useRef<HTMLEmbedElement>(null);
    const [refresh, setRefresh] = useState(false);
    const [hoverElement, setHoverElement] = useState(
        null as HTMLElement | null
    );
    const [show, setShow] = useState(false);

    const onCloseErrorModal = () => {
        setShow(false);
        setErrorModalProps({
            onClose: onCloseErrorModal,
        } as ErrorModalProps);
        // refresh the page
        setRefresh(true);
    };

    const [errorModalProps, setErrorModalProps] = useState({
        errorMessage: "",
        onClose: onCloseErrorModal,
    } as ErrorModalProps);

    const currentUser = Globals.currentUserValue;

    useEffect(() => {
        if (errorMessage !== "") {
            setErrorModalProps({
                ...errorModalProps,
                errorMessage: errorMessage,
            });
            setShow(true);
        } else {
            setShow(false);
        }
    }, [errorMessage]);

    useEffect(() => {
        OrganizationService.get({ id: currentUser.organizationID ?? "" }).then(
            (resp) => {
                if (resp.successful === true && resp.data) {
                    setOrganization(resp.data);
                }
            },
            (error) => {
                setErrorMessage(error.message);
            }
        );
    }, []);

    useEffect(() => {
        FileService.list({
            route: "splits",
        }).then(
            (resp) => {
                if (resp.successful === true && resp.data) {
                    setFiles(resp.data);
                }
            },
            (error) => {
                setErrorMessage(error.message);
            }
        );
    }, [refresh]);

    const handleMouseOver = (
        event: React.MouseEvent<HTMLImageElement>,
        file: APIModels.FileInfo
    ) => {
        if (file.filename!.indexOf(".pdf") > 0) {
            setHoverElement(event.currentTarget);

            FileService.get({
                submissionId: currentUser.id + "/splits",
                documentId: file.filename ?? "",
                source: "upload",
            }).then(
                (resp) => {
                    resp.json().then((json) => {
                        if (iframeRef && iframeRef.current)
                            iframeRef.current.src = json.url;
                    });
                },
                (error) => {
                    setErrorMessage(error.message);
                }
            );
        }
    };

    const onDrop = useCallback(
        (acceptedFiles) => {
            const filInfo = [...files];

            acceptedFiles.forEach((file: any) => {
                filInfo.push({ filename: file.name, size: file.size });

                // ensure that the file is a *.pdf or *.zip
                //    otherwise, display an error modal
                if (
                    !file.name.endsWith(".pdf") &&
                    !file.name.endsWith(".zip")
                ) {
                    setErrorMessage(
                        "Invalid File Type. Please Upload a PDF or ZIP File"
                    );
                    return;
                }

                FileService.upload({
                    fileServiceUrl: Endpoints.FileManager ?? "",
                    file: file,
                    filename: "splits/" + file.name,
                }).then(
                    (resp) => {
                        setRefresh(!refresh);
                        resp.text().then((text) => console.log(text));
                    },
                    (error) => {
                        setErrorMessage(error.message);
                    }
                );
            });

            setFiles(filInfo);
        },
        [files]
    );

    const handleMouseOut = (event: React.MouseEvent<HTMLImageElement>) => {
        if (event.currentTarget == hoverElement) {
            setHoverElement(null);
        }
    };

    const onFileUpload = (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage("");
        const input = event.target as HTMLInputElement;

        if (!input.files?.length) {
            return;
        }
        const temp = [...files];
        for (let i = 0; i < input.files.length; i++) {
            const f = input.files[i];
            if (f.name.includes(".zip") && !organization.batch) {
                setErrorMessage("Batch File Not Enabled For Account");
            } else {
                temp.push({ filename: f.name, size: f.size });
                setFiles(temp);
                if (
                    !f.name.toLowerCase().includes(".zip") &&
                    !f.name.toLowerCase().includes(".pdf")
                ) {
                    setErrorMessage(
                        "Invalid File Type. Please Upload a PDF or ZIP File"
                    );
                } else {
                    const sanatizedName = f.name.toLowerCase().includes(".zip")
                        ? f.name.toLowerCase().split(".zip")[0] + ".zip"
                        : f.name.toLowerCase().split(".pdf")[0] + ".pdf";
                    FileService.upload({
                        fileServiceUrl: Endpoints.FileManager ?? "",
                        file: f,
                        filename: "splits/" + sanatizedName,
                    }).then(
                        (resp) => {
                            setRefresh(!refresh);
                            input.value = "";
                        },
                        (error) => {
                            console.log("ERROR " + error);
                            input.value = "";
                        }
                    );
                }
            }
        }
    };

    const onDelete = (file: APIModels.FileInfo) => {
        if (file.filename) {
            FileService.delete({ filename: file.filename }).then(
                (resp) => {
                    setRefresh(!refresh);
                },
                (error) => {
                    console.log(error);
                }
            );
        }
    };

    const hoverPreview = () => {
        const rect = hoverElement!.getBoundingClientRect();
        return (
            <div
                style={{
                    position: "fixed",
                    top: rect.top,
                    left: rect.left,
                    height: "800px",
                    width: "800px",
                    backgroundColor: "white",
                    pointerEvents: "none",
                }}
            >
                <embed
                    ref={iframeRef}
                    type="application/pdf"
                    style={{ height: "800px", width: "800px" }}
                ></embed>
            </div>
        );
    };

    const onSelectFile = (index: number, file: APIModels.FileInfo) => {
        const matchingIndex = selectedFiles.findIndex(
            (v) => v.filename === file.filename
        );

        if (matchingIndex >= 0) {
            const filesUpdated = [...selectedFiles];
            filesUpdated.splice(matchingIndex, 1);
            setSelectedFiles(filesUpdated);
        } else {
            setSelectedFiles([...selectedFiles, file]);
        }

        return false;
    };

    const urlChange = ({
        path,
        s3loc,
        filename,
    }: {
        path: string;
        s3loc: string;
        filename: string;
    }) => {
        window.location.href = path + "/" + s3loc + "/" + filename;
    };

    const onDownload = (filename: string) => {
        setErrorMessage("");
        if (filename.includes(".zip")) {
            FileService.get({
                submissionId: currentUser.id + "/splits",
                documentId: filename,
                source: "upload",
            }).then(
                (resp) => {
                    resp.json().then((json) => {
                        // download the file using the presigned url from the json
                        const url = json.url;

                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute("download", filename);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                    });
                },
                (error) => {
                    setErrorMessage(error.message);
                }
            );
        } else {
            setErrorMessage(
                "Invalid File Type. Please select a ZIP File to view splitting results"
            );
        }
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        maxSize: 35000000,
        accept: "application/pdf",
        multiple: true,
    });

    return (
        <>
            {show && <ErrorModal {...errorModalProps} />}
            <div className="card-body">
                <div className="row">
                    <div className="col-sm-12">
                        <div
                            {...getRootProps({
                                className: "dropzone",
                            })}
                            style={{ border: "none" }}
                        >
                            <input {...getInputProps()} />
                            <div className="text-center">
                                <i className="h1 text-muted ri-hammer-fill"></i>
                                <h3 className="text-center">
                                    Drop files here or click button to
                                    preprocess.
                                </h3>
                            </div>

                            <div className="fileupload btn btn-success waves-effect waves-light mb-3">
                                <span>
                                    <i className="mdi mdi-cloud-upload me-1"></i>{" "}
                                    Add Files to Split
                                </span>
                                <input
                                    type="file"
                                    className="upload"
                                    onChange={onFileUpload}
                                    multiple
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div
                        className="table-responsive"
                        style={{ overflow: "visible" }}
                    >
                        {hoverElement && hoverPreview()}
                        <table
                            className="table table-centered  table-nowrap mb-0"
                            style={{ zIndex: 0 }}
                        >
                            <thead className="table-light">
                                <tr>
                                    <th></th>
                                    <th scope="col">File Name</th>
                                    <th scope="col">Date Modified</th>
                                    <th scope="col">Size</th>
                                    <th
                                        scope="col"
                                        className="text-center"
                                        style={{ width: 125 }}
                                    >
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {files.map((file, i) => {
                                    return (
                                        <tr>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        selectedFiles.filter(
                                                            (v) =>
                                                                v.filename ===
                                                                file.filename
                                                        ).length > 0
                                                    }
                                                    onChange={() =>
                                                        onSelectFile(i, file)
                                                    }
                                                />
                                            </td>
                                            <td>
                                                <img
                                                    src={
                                                        file.filename &&
                                                        file.filename
                                                            .toLowerCase()
                                                            .endsWith(".pdf")
                                                            ? pdfIcon
                                                            : zipIcon
                                                    }
                                                    height="30"
                                                    alt="icon"
                                                    className="me-2"
                                                    onMouseEnter={(e) =>
                                                        handleMouseOver(e, file)
                                                    }
                                                    onMouseLeave={
                                                        handleMouseOut
                                                    }
                                                />
                                                <a
                                                    href="javascript:void(0);"
                                                    className="text-dark"
                                                >
                                                    {file.filename}
                                                </a>
                                            </td>
                                            <td className="font-13">
                                                {file.lastUpdatedDateTime}
                                            </td>
                                            <td>
                                                {formatBytes(file.size ?? 0)}
                                            </td>
                                            {file.lastUpdatedDateTime ? (
                                                <td>
                                                    <>
                                                        <a
                                                            className="btn btn-success btn-sm"
                                                            href="#"
                                                            onClick={() =>
                                                                onDownload(
                                                                    file.filename!
                                                                )
                                                            }
                                                        >
                                                            <span className="ms-1">
                                                                Download
                                                            </span>
                                                        </a>{" "}
                                                        <a
                                                            className="btn btn-primary btn-sm"
                                                            href="#"
                                                            onClick={() =>
                                                                urlChange({
                                                                    path: "/admin/document",
                                                                    s3loc:
                                                                        currentUser.id ??
                                                                        "",
                                                                    filename:
                                                                        file?.filename ??
                                                                        "",
                                                                })
                                                            }
                                                        >
                                                            <i className="ri-magic-fill"></i>
                                                            <span> Split </span>
                                                        </a>
                                                    </>
                                                </td>
                                            ) : (
                                                <td>
                                                    <div
                                                        className="spinner-border text-primary m-2"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">
                                                            Loading...
                                                        </span>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
}
